import { POST_STATUS_DRAFT } from "../constants"
import { fetchAdvocatesAPIPath } from "../links"

export async function createPost(csrfToken, post) {
  try {
    let res = await fetch(`/api/posts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(post)
    })

    if (res.status === 401) {
      return {
        error: "unauthorized",
      }
    }

    let result = await res.json()
    if (res.status >= 400) {
      return {
        error: result.Error,
      }
    }

    return {
      post: result.Data,
    }
  } catch (e) {
    console.error("error while publishing post", e)
    return {
      error: "Error while creating post. Please try again in sometime."
    }
  }
}

export async function updatePost(csrfToken, post) {
  try {
    let res = await fetch(`/api/posts/${post.ID}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(post)
    })

    if (res.status === 401) {
      return {
        error: "unauthorized",
      }
    }

    let result = await res.json()
    if (res.status >= 400) {
      return {
        error: result.Error,
      }
    }

    return { post: result.Data }

  } catch (e) {
    console.error("error while updating post", e)
    return {
      error: "Error while updating post. Please try again in sometime."
    }
  }
}

export async function sharePost(csrfToken, post, variation, platform, postOverride, scheduledAt) {
  try {
    let res = await fetch(`/api/posts/${post.ID}/post-variations/${variation.ID}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ platform, scheduledAt: scheduledAt, postOverride })
    })
    let respBody = await res.json()
    if (res.status >= 400) {
      return {
        error: respBody.Error,
      }
    }
    return {
      socialMediaShare: respBody.Data,
    }
  } catch (e) {
    console.error("error while sharing post", e)
    return {
      error: "Error while sharing post. Please try again in sometime."
    }
  }
}

export async function fetchPosts({ orgID, userType, status, shareStatus, limit, offset, source, filters = { channelIDs: [], campaignIDs: [] } }) {
  try {
    const queryParams = new URLSearchParams({ userType, limit, offset })
    if (status !== POST_STATUS_DRAFT) {
      queryParams.append('postVariationStatus', "APPROVED")
    }
    if (shareStatus === "SHARED") {
      queryParams.append('shareStatuses', "SHARED")
      queryParams.append('shareStatuses', "FAILED")
      queryParams.append('shareStatuses', "PROCESSING")
    }
    if (shareStatus === "SCHEDULED") {
      queryParams.append('shareStatuses', "SCHEDULED")
    }
    if (status) {
      queryParams.append("status", status)
    }
    if (shareStatus === "PENDING_APPROVAL") {
      queryParams.set('postVariationStatus', "PENDING_APPROVAL")
    }
    if (filters.channelIDs?.length > 0) {
      queryParams.append("channelIDs", filters.channelIDs.join(","))
    }
    if (filters.campaignIDs?.length > 0) {
      queryParams.append("campaignIDs", filters.campaignIDs.join(","))
    }
    if (source) {
      queryParams.append("source", source)
    }
    if (orgID) {
      queryParams.append("orgID", orgID)
    }

    let res = await fetch(`/api/posts?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { posts: data.Data.Posts }
  } catch (e) {
    console.error("error while fetching posts", e)
    return {
      error: "Error while fetching posts. Please try again in sometime."
    }
  }
}

export async function fetchAdminPost({ postID, orgID }) {
  try {
    const queryParams = new URLSearchParams()
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    let res = await fetch(`/api/admin/posts/${postID}?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { post: data.Data }
  } catch (e) {
    console.error("error while fetching post", e)
    return {
      error: "Error while fetching post. Please try again in sometime."
    }
  }
}

export async function fetchSocialMediaShareForPost({ postID, orgID }) {
  try {
    const queryParams = new URLSearchParams({ postIDs: postID })
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    let res = await fetch(`/api/social-media-shares?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error }
    }
    return { socialMediaShare: data.Data?.[0] ?? null }
  } catch (e) {
    console.error("error while fetching social media share for post", e)
    return { error: "Error while fetching social media share. Please try again in sometime." }
  }
}

export async function fetchPost({ postID, orgID }) {
  try {
    const queryParams = new URLSearchParams()
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    let res = await fetch(`/api/posts/${postID}?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { post: data.Data }
  } catch (e) {
    console.error("error while fetching post", e)
    return {
      error: "Error while fetching post. Please try again in sometime."
    }
  }
}

export async function fetchPostByShareToken({ shareTokenID }) {
  try {
    let res = await fetch(`/api/public/posts/${shareTokenID}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { post: data.Data?.Post, socialMediaShare: data.Data?.SocialMediaShare, organization: data.Data?.Organization }
  } catch (e) {
    console.error("error while fetching post by share token", e)
    return {
      error: "Error while fetching post. Please try again in sometime."
    }
  }
}

export async function resumeShare({ shareTokenID }) {
  try {
    // make a post call
    let res = await fetch(`/api/public/posts/${shareTokenID}/resume-share`, {
      method: "POST",
    })
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }

    return { post: data.Data?.Post, socialMediaShare: data.Data?.SocialMediaShare }
  } catch (e) {
    console.error("error while resuming share", e)
    return {
      error: "Error while resuming share. Please try again in sometime."
    }
  }
}

export async function fetchAdvocate({ advocateID, orgID }) {
  try {
    const queryParams = new URLSearchParams()
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    let res = await fetch(`/api/admin/advocates/${advocateID}?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { advocate: data.Data }
  } catch (e) {
    console.error("error while fetching advocate", e)
    return {
      error: "Error while fetching advocate. Please try again in sometime."
    }
  }
}

export async function fetchAdvocates({ orgID, advocateIDs, includeSocialAccount }) {
  try {
    let res = await fetch(fetchAdvocatesAPIPath(orgID, advocateIDs, includeSocialAccount))
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { advocates: data.Data }
  } catch (e) {
    console.error("error while fetching advocates", e)
    return {
      error: "Error while fetching advocates. Please try again in sometime."
    }
  }
}

export async function fetchPostAnalytics({ postID, orgID, TimePeriodStart, TimePeriodEnd }) {
  try {
    const queryParams = new URLSearchParams()
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    if (TimePeriodStart && TimePeriodEnd) {
      queryParams.append("TimePeriodStart", TimePeriodStart)
      queryParams.append("TimePeriodEnd", TimePeriodEnd)
    }
    let res = await fetch(`/api/admin/posts/${postID}/analytics?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { analytics: data.Data }
  } catch (e) {
    console.error("error while fetching post analytics", e)
    return {
      error: "Error while fetching post analytics. Please try again in sometime."
    }
  }
}

export async function fetchAdvocateAnalytics({ advocateID, orgID, TimePeriodStart, TimePeriodEnd }) {
  try {
    const queryParams = new URLSearchParams()
    if (orgID) {
      queryParams.append("orgID", orgID)
    }
    if (TimePeriodStart && TimePeriodEnd) {
      queryParams.append("TimePeriodStart", TimePeriodStart)
      queryParams.append("TimePeriodEnd", TimePeriodEnd)
    }
    let res = await fetch(`/api/admin/advocates/${advocateID}/analytics?${queryParams.toString()}`)
    let data = await res.json()
    if (res.status >= 400) {
      return {
        error: data.Error,
      }
    }
    return { analytics: data.Data }
  } catch (e) {
    console.error("error while fetching advocate analytics", e)
    return {
      error: "Error while fetching advocate analytics. Please try again in sometime."
    }
  }
}

export async function deletePost(csrfToken, post) {
  try {
    let res = await fetch(`/api/posts/${post.ID}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status >= 400) {
      let result = await res.json()
      return {
        error: result.Error,
      }
    }
    return {}
  } catch (e) {
    console.error("error while deleting post", e)
    return {
      error: "Error while deleting post. Please try again in sometime."
    }
  }
}

export async function approvePost(csrfToken, _post, variation) {
  try {
    let res = await fetch(`/api/post-variations/${variation.ID}/approve`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
    if (res.status >= 400) {
      let result = await res.json()
      return {
        error: result.Error,
      }
    }
    return {}
  } catch (e) {
    console.error("error while approving post", e)
    return {
      error: "Error while approving post. Please try again in sometime."
    }
  }
}

export async function rejectPost(csrfToken, _post, variation, rejectionNotes) {
  try {
    let res = await fetch(`/api/post-variations/${variation.ID}/reject`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ RejectionNotes: rejectionNotes })
    })
    if (res.status >= 400) {
      let result = await res.json()
      return {
        error: result.Error,
      }
    }
    return {}
  } catch (e) {
    console.error("error while rejecting post", e)
    return {
      error: "Error while rejecting post. Please try again in sometime."
    }
  }
}

export async function externalSharePost(shareToken, variationID, postOverride) {
  try {
    let res = await fetch(`/api/public/posts/${shareToken}/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variationID, postOverride }),
    })
    let data = await res.json()
    if (res.status >= 400) {
      return { error: data.Error }
    }
    return { data: data.Data }
  } catch (e) {
    console.error("error while sharing post externally", e)
    return { error: "Error while sharing post. Please try again in sometime." }
  }
}
